import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ELEVATEOS_LOGO_ALT } from '@/constants'
import { useLocalStorage } from '@/hooks'

import {
  fetchBuildings,
  selectBuildingsForFilter,
  formatBuildingsForFilter,
  setBuildings,
  setActiveBuilding,
  selectActiveBuilding,
} from '../../reducers/buildingSelectorReducer'
import { selectUserUniqBuilding, selectUserData } from '../../reducers/userDataReducer'
import { getCommunicationPortalHistoryWithCount } from '../../reducers/communicationPortalReducer'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import Filter from '../Filter/Filter'
import { getLogoLink } from '../helpers'

const NavbarBuildingSelector = ({
  buildings,
  fetchBuildings,
  activeBuilding,
  setActiveBuilding,
  setBuildings,
  currentUserData,
}) => {
  const { t } = useTranslation(['navbar'])
  const { displaySnackbar } = useSnackbar()
  const currentRole = currentUserData.user.role
  const [localStorageActiveBuildingId, setLocalStorageActiveBuildingId] = useLocalStorage(
    `EL-user-active-builing-${currentUserData.id}`,
    null,
  )

  useEffect(() => {
    // NOTE: prefer the building the user last picked in this browser (localStorage) over the
    // account-level default, and resolve it in the *same* setBuildings/fetchBuildings dispatch
    // that seeds `buildings`. Previously this preference was applied via a separate follow-up
    // `setActiveBuilding` dispatch below, which meant `activeBuilding` transitioned twice on
    // initial load (default building, then the preferred one) - each transition re-fired every
    // effect keyed on `activeBuilding` (e.g. PostHog group identify).
    const preferredBuildingId = localStorageActiveBuildingId ?? currentUserData.defaultBuildingId

    if (['PropertyManager', 'Staff'].includes(currentRole)) {
      setBuildings(currentUserData.buildingIds, preferredBuildingId)
    } else if (currentRole === 'Trainer') {
      setBuildings(null, preferredBuildingId)
    } else if (currentRole === 'ShiftManager') {
      setBuildings(buildings, preferredBuildingId)
    } else {
      fetchBuildings(preferredBuildingId).catch(() =>
        displaySnackbar(t('navbar:buildingSelector.fetchError'), true),
      )
    }
  }, [])

  useEffect(() => {
    if (!buildings?.length) return

    const matchingBuilding = buildings.find(
      (building) => building.id === localStorageActiveBuildingId,
    )

    // Already resolved by the mount effect above (or nothing to switch to) - skip the dispatch
    // to avoid a redundant `activeBuilding` transition. This still covers later changes, e.g. the
    // user switching buildings in another tab (`useLocalStorage`'s `storage` event listener).
    if (!matchingBuilding || matchingBuilding.id === activeBuilding?.id) {
      return
    }

    setActiveBuilding(matchingBuilding)
  }, [buildings, localStorageActiveBuildingId, activeBuilding, setActiveBuilding])

  if (!activeBuilding) return null
  if (buildings.length === 1) {
    return (
      <div className="navmgr-logo">
        <Link to={getLogoLink(currentUserData, currentUserData.user.role)}>
          <img
            className="chitown-logo"
            src="/images/logo/elevateos-logo-wide-white.svg"
            alt={ELEVATEOS_LOGO_ALT}
          />
        </Link>
      </div>
    )
  }

  return (
    <Filter
      placeholder={activeBuilding ? activeBuilding.text : t('navbar:buildingSelector.placeholder')}
      buildingImage={activeBuilding ? activeBuilding.avatar : ''}
      options={buildings}
      disabled={!buildings || buildings.length < 1}
      value={activeBuilding ? [activeBuilding] : []}
      name={'Building'}
      additionalClass={'navbar__filter'}
      variant={'rounded'}
      config={{ hasSearch: true }}
      onChange={(e) => {
        setActiveBuilding(e.target.value)
        setLocalStorageActiveBuildingId(e.target.value.id)
      }}
    />
  )
}

NavbarBuildingSelector.propTypes = {
  buildings: PropTypes.array,
  activeBuilding: PropTypes.object,
  fetchBuildings: PropTypes.func,
  setActiveBuilding: PropTypes.func,
  setBuildings: PropTypes.func,
  currentUserData: PropTypes.object,
}

export default connect(
  (state) => {
    const currentUserData = selectUserData(state)
    return {
      currentUserData: selectUserData(state),
      activeBuilding: selectActiveBuilding(state),
      buildings:
        currentUserData.user.role === 'ShiftManager'
          ? formatBuildingsForFilter([selectUserUniqBuilding(state)])
          : selectBuildingsForFilter(state),
    }
  },
  { fetchBuildings, getCommunicationPortalHistoryWithCount, setBuildings, setActiveBuilding },
)(NavbarBuildingSelector)
